import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { engagementsApi } from '../../api/engagements.js';
import { speakersApi } from '../../api/speakers.js';
import { eventsApi } from '../../api/events.js';
import { FormField, Input, Select, Textarea } from '../../components/common/FormField.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';
import { ENGAGEMENT_STATUSES } from '../../utils/constants.js';

export function EngagementFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  usePageHeader(isEdit ? 'Edit talk' : 'New engagement');

  const [form, setForm] = useState({
    speakerId: searchParams.get('speakerId') ?? '',
    eventId: searchParams.get('eventId') ?? '',
    status: 'Identified',
    talkTitle: '',
    talkAbstract: '',
    speakerNotes: '',
    coordinationNotes: '',
    nextDueDate: '',
  });
  const [speakerLocked, setSpeakerLocked] = useState(null); // { firstName, lastName } — shown read-only in edit mode
  const [eventLocked, setEventLocked] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [events, setEvents] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loads = [speakersApi.list(), eventsApi.list()];
    if (isEdit) loads.push(engagementsApi.get(id));
    Promise.all(loads)
      .then(([spks, evts, eng]) => {
        setSpeakers(spks ?? []);
        setEvents(evts ?? []);
        if (eng) {
          setForm({
            speakerId: eng.speakerId ?? '',
            eventId: eng.eventId ?? '',
            status: eng.status ?? 'Identified',
            talkTitle: eng.talkTitle ?? '',
            talkAbstract: eng.talkAbstract ?? '',
            speakerNotes: eng.speakerNotes ?? '',
            coordinationNotes: eng.coordinationNotes ?? '',
            nextDueDate: eng.nextDueDate ?? '',
          });
          setSpeakerLocked((spks ?? []).find((s) => s.id === eng.speakerId) ?? null);
          setEventLocked((evts ?? []).find((e) => e.id === eng.eventId) ?? null);
        }
      })
      .catch((e) => setErrors({ _: e.message }))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!isEdit) {
      const e = {};
      if (!form.speakerId) e.speakerId = 'Speaker is required';
      if (!form.eventId) e.eventId = 'Event is required';
      if (Object.keys(e).length) { setErrors(e); return; }
    }
    setSaving(true);
    try {
      if (isEdit) {
        await engagementsApi.update(id, form);
        navigate(`/engagements/${id}`);
      } else {
        const eng = await engagementsApi.create(form);
        navigate(`/engagements/${eng.id}`);
      }
    } catch (err) {
      setErrors({ _: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="page-body">
        <ErrorAlert error={errors._} />
        <form onSubmit={handleSubmit} noValidate style={{ maxWidth: 540 }}>
          <div className="stack">
            {isEdit ? (
              <FormField label="Speaker" id="speakerId">
                <Input id="speakerId" value={speakerLocked ? `${speakerLocked.firstName} ${speakerLocked.lastName}` : '—'} disabled />
              </FormField>
            ) : (
              <FormField label="Speaker" required id="speakerId" error={errors.speakerId}>
                <Select id="speakerId" value={form.speakerId} onChange={set('speakerId')} error={errors.speakerId} required>
                  <option value="">Select speaker…</option>
                  {speakers.map((s) => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </Select>
              </FormField>
            )}
            {isEdit ? (
              <FormField label="Event" id="eventId">
                <Input id="eventId" value={eventLocked ? `${eventLocked.name} (${eventLocked.date})` : 'No event assigned yet'} disabled />
              </FormField>
            ) : (
              <FormField label="Event" required id="eventId" error={errors.eventId}>
                <Select id="eventId" value={form.eventId} onChange={set('eventId')} error={errors.eventId} required>
                  <option value="">Select event…</option>
                  {events.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.date})</option>)}
                </Select>
              </FormField>
            )}
            <FormField label="Status" id="status">
              <Select id="status" value={form.status} onChange={set('status')}>
                {ENGAGEMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </FormField>
            <FormField label="Talk title" id="talkTitle">
              <Input id="talkTitle" value={form.talkTitle} onChange={set('talkTitle')} />
            </FormField>
            <FormField label="Talk abstract" id="talkAbstract">
              <Textarea id="talkAbstract" value={form.talkAbstract} onChange={set('talkAbstract')} rows={4} />
            </FormField>
            <FormField label="Next due date" id="nextDueDate">
              <Input id="nextDueDate" type="date" value={form.nextDueDate} onChange={set('nextDueDate')} />
            </FormField>
            <FormField label="Speaker notes" id="speakerNotes" hint="Visible to the speaker via their portal">
              <Textarea id="speakerNotes" value={form.speakerNotes} onChange={set('speakerNotes')} rows={2} />
            </FormField>
            <FormField label="Coordination notes" id="coordinationNotes" hint="Chapter-internal only">
              <Textarea id="coordinationNotes" value={form.coordinationNotes} onChange={set('coordinationNotes')} rows={2} />
            </FormField>
            <div className="inline" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create engagement'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
